/**
 * @module useFlight
 */
import { useCallback } from 'react'
import {
  useGetFlightMutation,
  useGetFlightsMutation,
  useGetFlightByFlightNumberMutation,
  useCreateFlightMutation,
  useUpdateFlightMutation,
  useDeleteFlightMutation,
  useUpdateFlightStatusMutation,
  useBookSeatsMutation,
  useCancelSeatsMutation,
  useGetFlightsByAirlineQuery,
} from '../services/flight.service'

export const useFlight = () => {
  const [executeGetFlights, { isLoading: isGetFlightsLoading }] = useGetFlightsMutation()
  const [executeGetFlight, { isLoading: isGetFlightLoading }] = useGetFlightMutation()
  const [executeGetFlightByFlightNumber, { isLoading: isGetFlightByFlightNumberLoading }] =
    useGetFlightByFlightNumberMutation()
  const [executeCreateFlight, { isLoading: isCreateFlightLoading }] = useCreateFlightMutation()
  const [executeUpdateFlight, { isLoading: isUpdateFlightLoading }] = useUpdateFlightMutation()
  const [executeDeleteFlight, { isLoading: isDeleteFlightLoading }] = useDeleteFlightMutation()
  const [executeUpdateFlightStatus, { isLoading: isUpdateFlightStatusLoading }] =
    useUpdateFlightStatusMutation()
  const [executeBookSeats, { isLoading: isBookSeatsLoading }] = useBookSeatsMutation()
  const [executeCancelSeats, { isLoading: isCancelSeatsLoading }] = useCancelSeatsMutation()

  const getFlightsAsync = useCallback(async () => {
    if (isGetFlightsLoading) {
      return
    }
    try {
      const response = await executeGetFlights().unwrap()
      return response.data.flights
    } catch (error) {
      console.error('#getFlightsAsync: Error fetching flights', error)
      throw error
    }
  }, [executeGetFlights, isGetFlightsLoading])

  const getFlightAsync = useCallback(
    async (flightId) => {
      if (isGetFlightLoading) {
        return
      }
      try {
        const response = await executeGetFlight(flightId).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#getFlightAsync: Error fetching flight', error)
        throw error
      }
    },
    [executeGetFlight, isGetFlightLoading],
  )

  const getFlightByFlightNumberAsync = useCallback(
    async (flightNumber) => {
      if (isGetFlightByFlightNumberLoading) {
        return
      }
      try {
        const response = await executeGetFlightByFlightNumber(flightNumber).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#getFlightByFlightNumberAsync: Error fetching flight', error)
        throw error
      }
    },
    [executeGetFlightByFlightNumber, isGetFlightByFlightNumberLoading],
  )

  const createFlightAsync = useCallback(
    async (flight) => {
      if (isCreateFlightLoading) {
        return
      }
      try {
        const response = await executeCreateFlight(flight).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#createFlightAsync: Error creating flight', error)
        throw error
      }
    },
    [executeCreateFlight, isCreateFlightLoading],
  )

  const updateFlightAsync = useCallback(
    async (flightId, updates) => {
      if (isUpdateFlightLoading) {
        return
      }
      try {
        const response = await executeUpdateFlight({ flightId, ...updates }).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#updateFlightAsync: Error updating flight', error)
        throw error
      }
    },
    [executeUpdateFlight, isUpdateFlightLoading],
  )

  const deleteFlightAsync = useCallback(
    async (flightId) => {
      if (isDeleteFlightLoading) {
        return
      }
      try {
        const response = await executeDeleteFlight(flightId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteFlightAsync: Error deleting flight', error)
        throw error
      }
    },
    [executeDeleteFlight, isDeleteFlightLoading],
  )

  const updateFlightStatusAsync = useCallback(
    async (flightId, status) => {
      if (isUpdateFlightStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateFlightStatus({ flightId, status }).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#updateFlightStatusAsync: Error updating flight status', error)
        throw error
      }
    },
    [executeUpdateFlightStatus, isUpdateFlightStatusLoading],
  )

  const bookSeatsAsync = useCallback(
    async (flightId, count) => {
      if (isBookSeatsLoading) {
        return
      }
      try {
        const response = await executeBookSeats({ flightId, count }).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#bookSeatsAsync: Error booking seats', error)
        throw error
      }
    },
    [executeBookSeats, isBookSeatsLoading],
  )

  const cancelSeatsAsync = useCallback(
    async (flightId, seatIds) => {
      if (isCancelSeatsLoading) {
        return
      }
      try {
        const response = await executeCancelSeats({ flightId, seatIds }).unwrap()
        return response.data.flight
      } catch (error) {
        console.error('#cancelSeatsAsync: Error canceling seats', error)
        throw error
      }
    },
    [executeCancelSeats, isCancelSeatsLoading],
  )

  return {
    getFlightsAsync,
    getFlightAsync,
    getFlightByFlightNumberAsync,
    createFlightAsync,
    updateFlightAsync,
    deleteFlightAsync,
    updateFlightStatusAsync,
    bookSeatsAsync,
    cancelSeatsAsync,
    isGetFlightsLoading,
    isGetFlightLoading,
    isGetFlightByFlightNumberLoading,
    isCreateFlightLoading,
    isUpdateFlightLoading,
    isDeleteFlightLoading,
    isUpdateFlightStatusLoading,
    isBookSeatsLoading,
    isCancelSeatsLoading,
  }
}

// Separate hook for airline-specific flights
export const useFlightsByAirline = (airlineId) => {
  const { data, isLoading, error, refetch } = useGetFlightsByAirlineQuery(airlineId, {
    skip: !airlineId,
  })

  return {
    flights: data?.data?.flights || [],
    isLoading,
    error,
    refetch,
  }
}
